import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import moment from 'moment';
import _ from 'underscore';
import ClipboardListIcon from 'components/v2/icons/ClipboardListIcon';
import { getAppointmentStatusMetrics, cancelAppointment, updateAppointmentStatus, getAppointments, getAppointmentLocations, } from 'utils/AppointmentWebAPIUtils';
import FetchWebAPIUtils from 'utils/FetchWebAPIUtils';
import { handleDataStream, convertJsonToCSV } from 'utils/StreamHandlingUtils';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import Container from 'components/v2/container';
import Pagination from 'components/v2/Pagination';
import { titleize } from 'underscore.string';
import SessionStore from '../../../stores/SessionStore';
import Filters from './Filters';
import AppointmentsList from './AppointmentsList';
import AppointmentCalendarMetrics from './AppointmentCalendarMetrics';
import ActiveFilters from './ActiveFilters';
import ProTip from '../../ui/ProTip';
import COLUMNS from './columns';
import CarrierValidationModal from './CarrierValidationModal';
import BulkContainerAvailabilityModal from './BulkContainerAvailabilityUploadModal';
import DropdownMenu from '../../v2/dropdowns/DropdownMenu';
import DropdownItem from '../../v2/dropdowns/DropdownItem';
const PER_PAGE = 20;
const DEFAULT_SORT_COLUMN = 'updates';
const ASCENDING = 'asc';
const DESCENDING = 'desc';
const FILTERS = {
    _ID: '_id',
    YARD: 'yard',
    STATUS: 'status',
    CONSIGNEE: 'consignee',
    SHIPPER: 'shipper',
    CARRIER: 'carrier',
    CONTAINER_NUMBER: 'container_number',
    MASTER_BILL: 'master_bill_number',
    PAGINATION: 'pagination',
    DATE_RANGE: 'daterange',
    WINDOW_START: 'window_start',
    WINDOW_END: 'window_end',
    CALENDAR: 'calendar',
    SHIPPER_REMOVE: 'shipper_remove',
    CARRIER_REMOVE: 'carrier_remove',
    CONSIGNEE_REMOVE: 'consignee_remove',
    SORT_BY_REMOVE: 'sort_by_remove',
    STATUS_REMOVE: 'status_remove',
    YARD_REMOVE: 'yard_remove',
    CONTAINER_REMOVE: 'container_remove',
    MASTER_BILL_REMOVE: 'master_bill_remove',
};
const ADMIN = 'Admin';
const CARRIER = 'Carrier';
export default function AppointmentModule() {
    const { t } = useTranslation();
    let history = useHistory();
    let abortController;
    const [showProTip, setShowPropTip] = useState(true);
    const [appointmentStatuses, setAppointmentStatuses] = useState([
        { name: t('APPROVED'), slug: 'approved' },
        { name: t('CANCELED'), slug: 'canceled' },
        { name: t('DECLINED'), slug: 'declined' },
        { name: t('PENDING'), slug: 'pending' },
        { name: t('COMPLETE'), slug: 'complete' },
        { name: t('MISSED'), slug: 'missed' },
    ]);
    const defaultQueryParams = {
        page: 1,
        perPage: PER_PAGE,
        sortBy: DEFAULT_SORT_COLUMN,
        sortOrder: DESCENDING,
    };
    function handleSessionChange() {
        const updatedUserType = SessionStore.getUserType();
        setUserType(updatedUserType);
        const mappedColumns = COLUMNS.filter((c) => {
            return c.show && c.users.includes(updatedUserType);
        });
        setColumns(mappedColumns);
    }
    useEffect(() => {
        handleSessionChange();
        SessionStore.addChangeListener(handleSessionChange);
        return () => {
            SessionStore.removeChangeListener(handleSessionChange);
        };
    }, []);
    const [userType, setUserType] = useState(SessionStore.getUserType());
    const [appointmentFacilities, setAppointmentFacilities] = useState([]);
    const [, setExporting] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [paginationCount, setPaginationCount] = useState(0);
    const [paginationPages, setPaginationPages] = useState(0);
    const [query, setQuery] = useState(defaultQueryParams);
    const [columns, setColumns] = useState([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showBulkContainerAvailabilityModal, setShowBulkContainerAvailabilityModal,] = useState(false);
    const [confirmationModalMessage, setConfirmationModalMessage] = useState('');
    const [confirmationModalAction, setConfirmationModalAction] = useState(() => { });
    const [loaded, setLoaded] = useState(false);
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
    const [statusMetrics, setStatusMetrics] = useState([]);
    const [showCarrierValidationModal, setShowCarrierValidationModal] = useState(false);
    function transformQuery(query) {
        const ID_ITEMS = ['_id', 'yard', 'consignee', 'carrier', 'shipper'];
        let transformedQuery = {};
        _.map(query, (val, key) => {
            if (!_.isEmpty(val)) {
                if (key === 'status') {
                    transformedQuery[key] = val.map((v) => {
                        return v.slug;
                    });
                }
                else if (ID_ITEMS.includes(key)) {
                    transformedQuery[key] = val.map((v) => {
                        return v._id;
                    });
                }
                else {
                    transformedQuery[key] = val;
                }
            }
            else if (key === 'page' || key === 'perPage') {
                transformedQuery[key] = val;
            }
        });
        return transformedQuery;
    }
    function onStatusActionClick(_id, newStatus) {
        const statusMap = newStatus === 'declined' && userType !== ADMIN ? 'canceled' : newStatus;
        setConfirmationModalMessage(t('MOVE_APPOINTMENT_STATUS_CONFIRMATION', { status: statusMap }));
        setConfirmationModalAction(() => {
            return async function () {
                try {
                    const result = statusMap === 'canceled'
                        ? await cancelAppointment(_id)
                        : await updateAppointmentStatus(_id, newStatus);
                    if (result) {
                        const newItems = appointments.map((a) => {
                            if (a._id === result._id) {
                                a.status = result.status;
                            }
                            return a;
                        });
                        setAppointments([...newItems]);
                    }
                }
                finally {
                    setShowConfirmationModal(false);
                }
            };
        });
        setShowConfirmationModal(true);
    }
    function onEditActionClick(_id) {
        history.push(`/appointments/${_id}`);
    }
    function onFilterChanged(type, value, toRemove) {
        switch (type) {
            case FILTERS.WINDOW_START: {
                setQuery(Object.assign(Object.assign({}, query), value));
                break;
            }
            case FILTERS.WINDOW_END: {
                setQuery(Object.assign(Object.assign({}, query), value));
                break;
            }
            case FILTERS.CALENDAR: {
                const { start: window_start, end: window_end, status } = value;
                const update = {
                    window_start: moment(window_start).startOf('day').toISOString(),
                    window_end: moment(window_end).endOf('day').toISOString(),
                };
                setSelectedCalendarDate(window_start);
                if (status) {
                    update.status = [{ slug: status }];
                }
                else {
                    update.status = undefined;
                }
                setQuery(Object.assign(Object.assign({}, query), update));
                break;
            }
            case FILTERS.DATE_RANGE: {
                const { start, end } = value;
                if (!start && !end) {
                    setQuery(Object.assign({}, _.omit(query, ['window_start', 'window_end'])));
                }
                else {
                    setQuery(Object.assign(Object.assign({}, query), { window_start: moment(start).startOf('day').toISOString(), window_end: moment(end).endOf('day').toISOString() }));
                }
                break;
            }
            case FILTERS.PAGINATION: {
                setQuery(Object.assign(Object.assign({}, query), { page: value }));
                break;
            }
            case FILTERS.YARD: {
                if (_.isEmpty(value)) {
                    let filter = query;
                    delete filter.yard;
                    return setQuery(Object.assign({}, filter));
                }
                const yardIds = value.map((v) => {
                    return v._id;
                });
                setQuery(Object.assign(Object.assign({}, query), { yard: value }));
                const appointmentFacilitiesUpdate = appointmentFacilities.map((s) => {
                    s.selected = yardIds.includes(s._id);
                    return s;
                });
                setAppointmentFacilities(appointmentFacilitiesUpdate);
                break;
            }
            case FILTERS.STATUS: {
                if (_.isEmpty(value)) {
                    let filter = query;
                    delete filter.status;
                    return setQuery(Object.assign({}, filter));
                }
                const selectedStatuses = value.map((v) => {
                    return v.slug;
                });
                setQuery(Object.assign(Object.assign({}, query), { status: value }));
                const appointmentStatusUpdate = appointmentStatuses.map((s) => {
                    s.selected = selectedStatuses.includes(s.slug);
                    return s;
                });
                setAppointmentStatuses(appointmentStatusUpdate);
                break;
            }
            case FILTERS.CONSIGNEE: {
                const consignees = query.consignee || [];
                consignees.push(value);
                setQuery(Object.assign(Object.assign({}, query), { consignee: consignees }));
                break;
            }
            case FILTERS.SHIPPER: {
                const shippers = query.shipper || [];
                shippers.push(value);
                setQuery(Object.assign(Object.assign({}, query), { shipper: shippers }));
                break;
            }
            case FILTERS.CARRIER: {
                const carriers = query.carrier || [];
                carriers.push(value);
                setQuery(Object.assign(Object.assign({}, query), { carrier: carriers }));
                break;
            }
            case FILTERS.CONTAINER_NUMBER: {
                const containers = query.container_number || [];
                containers.push(value.container_number);
                setQuery(Object.assign(Object.assign({}, query), { container_number: containers }));
                break;
            }
            case FILTERS.SORT_BY_REMOVE: {
                setQuery(Object.assign(Object.assign({}, query), { sortBy: undefined, sortOrder: undefined }));
                break;
            }
            case FILTERS.STATUS_REMOVE: {
                let { status } = query;
                status =
                    status && status.length
                        ? status.filter((s) => {
                            return s.slug !== toRemove;
                        })
                        : undefined;
                setQuery(Object.assign(Object.assign({}, query), { status }));
                break;
            }
            case FILTERS.CONSIGNEE_REMOVE: {
                let { consignee } = query;
                consignee =
                    consignee && consignee.length
                        ? consignee.filter((c) => {
                            return c._id !== toRemove;
                        })
                        : undefined;
                setQuery(Object.assign(Object.assign({}, query), { consignee }));
                break;
            }
            case FILTERS.SHIPPER_REMOVE: {
                let { shipper } = query;
                shipper =
                    shipper && shipper.length
                        ? shipper.filter((s) => {
                            return s._id !== toRemove;
                        })
                        : undefined;
                setQuery(Object.assign(Object.assign({}, query), { shipper }));
                break;
            }
            case FILTERS.CARRIER_REMOVE: {
                let { carrier } = query;
                carrier =
                    carrier && carrier.length
                        ? carrier.filter((c) => {
                            return c._id !== toRemove;
                        })
                        : undefined;
                setQuery(Object.assign(Object.assign({}, query), { carrier }));
                break;
            }
            case FILTERS.YARD_REMOVE: {
                let { yard } = query;
                yard =
                    yard && yard.length
                        ? yard.filter((y) => {
                            return y._id !== toRemove;
                        })
                        : undefined;
                setQuery(Object.assign(Object.assign({}, query), { yard }));
                break;
            }
            case FILTERS.MASTER_BILL: {
                const masters = query.master_bill_number || [];
                masters.push(value.master_bill_number);
                setQuery(Object.assign(Object.assign({}, query), { master_bill_number: masters }));
                break;
            }
            case FILTERS.CONTAINER_REMOVE: {
                let { container_number } = query;
                container_number =
                    container_number && container_number.length > 0
                        ? _.without(container_number, toRemove)
                        : undefined;
                setQuery(Object.assign(Object.assign({}, query), { container_number: container_number }));
                break;
            }
            case FILTERS.MASTER_BILL_REMOVE: {
                let { master_bill_number } = query;
                master_bill_number =
                    master_bill_number && master_bill_number.length > 0
                        ? _.without(master_bill_number, toRemove)
                        : undefined;
                setQuery(Object.assign(Object.assign({}, query), { master_bill_number: master_bill_number }));
                break;
            }
            default: {
                console.log('Unhandled Filter Change!', type, value);
            }
        }
    }
    function onHeaderClick(column) {
        // Columns eligible for sorting in configuration
        if (column.sort) {
            let newSortOrder = query.sortOrder;
            // If column is currently sorted, invert it
            if (query.sortBy === column.attribute) {
                newSortOrder = query.sortOrder === ASCENDING ? DESCENDING : ASCENDING;
            }
            else {
                // Set new column order
                newSortOrder = ASCENDING;
            }
            setQuery(Object.assign(Object.assign({}, query), { sortBy: column.attribute, sortOrder: newSortOrder }));
        }
    }
    useEffect(() => {
        async function queryAppointmentFacilities() {
            const facilities = await getAppointmentLocations();
            if (checkForDifferentFacilities(facilities)) {
                setAppointmentFacilities(facilities);
            }
        }
        async function queryStatusMetrics() {
            const start = moment().subtract(1, 'days').startOf('day').toISOString();
            const end = moment().endOf('day').add(7, 'days').toISOString();
            const metrics = await getAppointmentStatusMetrics(start, end);
            setStatusMetrics(metrics);
        }
        queryAppointmentFacilities();
        queryStatusMetrics();
    }, [appointments]);
    const checkForDifferentFacilities = (facilities) => {
        if (!appointmentFacilities.length) {
            return true;
        }
        const fIds = appointmentFacilities.map((f) => f._id);
        return facilities.some((f) => !fIds.includes(f._id));
    };
    const debouncedQuery = useMemo(() => {
        return _.debounce(async (query) => {
            const filter = transformQuery(query);
            const result = await getAppointments(filter);
            if (result) {
                const { items, totalCount, totalPages } = result;
                setAppointments(items);
                setPaginationCount(totalCount);
                setPaginationPages(totalPages);
                setLoaded(true);
            }
            else {
                setPaginationCount(0);
                setPaginationPages(1);
                setAppointments([]);
                setLoaded(true);
            }
        }, 200);
    }, []);
    useEffect(() => {
        debouncedQuery(query);
    }, [query]);
    const hasAppointments = appointments && appointments.length > 0;
    async function exportCSV() {
        setExporting(true);
        if (abortController) {
            await abortController();
        }
        const mapQuery = () => {
            return Object.keys(query).reduce((finalQuery, key) => {
                var _a, _b, _c, _d;
                if (['container_number', 'master_bill_number'].includes(key)) {
                    finalQuery[key] = query[key].pop();
                    return finalQuery;
                }
                if (['yard', 'consignee', 'shipper', 'carrier'].includes(key)) {
                    finalQuery[key] = (_b = (_a = query[key]) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b._id;
                    return finalQuery;
                }
                if (key === 'status') {
                    finalQuery[key] = (_d = (_c = query[key]) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.slug;
                    return finalQuery;
                }
                finalQuery[key] = query[key];
                return finalQuery;
            }, {});
        };
        const { response, controller } = await FetchWebAPIUtils.getScheduledYardAppointmentsCSV(mapQuery());
        abortController = controller;
        handleDataStream(response, handleStreamEnd);
    }
    const handleStreamEnd = (rows) => {
        const filename = 'appointments.csv';
        setExporting(false);
        return convertJsonToCSV(rows, filename);
    };
    function onAppointmentClick() {
        if (userType !== CARRIER) {
            history.push('/appointments/new');
        }
        else {
            setShowCarrierValidationModal(true);
        }
    }
    function handleSelectAction(action) {
        switch (action) {
            case 'update_container_availability':
                return setShowBulkContainerAvailabilityModal(true);
            case 'export':
                return exportCSV();
            default:
                break;
        }
    }
    function handleClosePropTip() {
        setShowPropTip(false);
    }
    return (React.createElement(Container, { className: "pb-32", useMaxWidth: false },
        React.createElement("div", { className: "flex flex-col space-y-4" },
            React.createElement("div", { className: "mt-6 flex flex-wrap lg:-mx-4" },
                React.createElement("div", { className: "lg:w-2/3 w-full mb-4 lg:mb-0 lg:px-4" }, showProTip && (React.createElement(ProTip, { message: t('PRO_TIP_APPOINTMENT_HEADER'), onClose: handleClosePropTip }))),
                React.createElement("div", { className: "lg:w-1/3 w-full flex justify-end space-x-2 lg:px-4" },
                    React.createElement("div", null,
                        React.createElement("button", { className: "button button-green flex inline", onClick: onAppointmentClick },
                            React.createElement(ClipboardListIcon, null),
                            t('NEW_APPOINTMENT'))),
                    React.createElement(DropdownMenu, { title: titleize(t('ACTIONS')) },
                        React.createElement(Menu.Item, { id: "export", key: "export" },
                            React.createElement(DropdownItem, { onClick: () => handleSelectAction('export') }, titleize(t('EXPORT')))),
                        userType === 'Admin' && (React.createElement(Menu.Item, { key: "update_container_availability" },
                            React.createElement(DropdownItem, { onClick: () => handleSelectAction('update_container_availability') }, t('UPDATE_CONTAINER_AVAILABILITY'))))))),
            React.createElement("div", { className: "flex mt-6" },
                false && (React.createElement("div", { className: "flex items-center cursor-pointer" },
                    React.createElement("i", { className: "fa fa-chevron-left" }))),
                React.createElement("div", { className: "flex flex-1 overflow-x-auto space-x-2 sm:space-x-4" },
                    React.createElement(AppointmentCalendarMetrics, { onCalendarClick: (data) => onFilterChanged('calendar', data), selectedDate: selectedCalendarDate, metrics: statusMetrics })),
                false && (React.createElement("div", { className: "flex items-center cursor-pointer" },
                    React.createElement("i", { className: "fa fa-chevron-right" })))),
            React.createElement("div", { className: "p-4 rounded bg-grey-light" }, loaded && (React.createElement(Filters, { onFilterChanged: onFilterChanged, appointmentStatuses: appointmentStatuses, appointmentFacilities: appointmentFacilities }))),
            React.createElement("div", { className: "flex" },
                React.createElement(ActiveFilters, { query: query, onRemove: onFilterChanged, onClear: () => setQuery({}) })),
            hasAppointments && (React.createElement("div", null,
                React.createElement(Pagination, { key: 1, perPage: PER_PAGE, page: query.page, pages: paginationPages, total: paginationCount, onPageClick: (page) => onFilterChanged('pagination', page) }),
                React.createElement(AppointmentsList, { sortColumn: query.sortBy, sortOrder: query.sortOrder, appointments: appointments, userType: userType, columns: columns, onHeaderClick: onHeaderClick, onStatusActionClick: onStatusActionClick, onEditActionClick: onEditActionClick }))),
            loaded && !hasAppointments && (React.createElement("div", { className: "p-4" }, t('NO_APPOINTMENTS_FOUND'))),
            !loaded && (React.createElement("div", { className: "flex w-full justify-center align-middle" },
                React.createElement("i", { className: "fa fa-spinner fa-spin text-5xl text-green" }))),
            React.createElement(ConfirmModal, { show: showConfirmationModal, title: titleize(t('CONFIRM')), message: confirmationModalMessage, onConfirm: () => confirmationModalAction(), onHide: () => setShowConfirmationModal(false), affirmative: titleize(t('CONFIRM')), hideButtonText: titleize(t('CANCEL')), primaryButtonColor: "green" }),
            React.createElement(CarrierValidationModal, { show: showCarrierValidationModal, onHideModal: () => setShowCarrierValidationModal(false) }),
            React.createElement(BulkContainerAvailabilityModal, { showModal: showBulkContainerAvailabilityModal, onHide: () => setShowBulkContainerAvailabilityModal(false) }))));
}
